import React, { useState, useEffect, useRef, useCallback } from 'react';
import { playSuccessSound, playDuplicateSound, playDivergenceSound, playErrorSound } from '../../utils/soundAlerts';
import { useParams, useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
// @ts-ignore - TS 4.9.5 can't resolve all exports from lucide-react's large barrel d.ts
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left';
// @ts-ignore
import ScanLine from 'lucide-react/dist/esm/icons/scan-line';
// @ts-ignore
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
// @ts-ignore
import XCircle from 'lucide-react/dist/esm/icons/x-circle';
// @ts-ignore
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
// @ts-ignore
import Copy from 'lucide-react/dist/esm/icons/copy';
// @ts-ignore
import PackageCheck from 'lucide-react/dist/esm/icons/package-check';
// @ts-ignore
import X from 'lucide-react/dist/esm/icons/x';
// @ts-ignore
import CloudOff from 'lucide-react/dist/esm/icons/cloud-off';
// @ts-ignore
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw';
// @ts-ignore
import WifiOff from 'lucide-react/dist/esm/icons/wifi-off';
// @ts-ignore
import Camera from 'lucide-react/dist/esm/icons/camera';
// @ts-ignore
import Crosshair from 'lucide-react/dist/esm/icons/crosshair';
// @ts-ignore
import MapPin from 'lucide-react/dist/esm/icons/map-pin';
import {
  buscarConferenciaConferente, registrarLeituraConferente,
  finalizarConferenciaConferente, listarLeiturasConferente
} from '../../services/conferente';
import type { Conferencia, ConferenciaLeitura, ResumoLeituras } from '../../types/conferencia';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import {
  salvarLeituraPendente, listarLeiturasPendentes, contarLeiturasPendentes,
  sincronizarPendentes, salvarConferenciaCache, buscarConferenciaCache,
  listarConferenciasPendentes,
  type LeituraPendente
} from '../../services/offlineQueue';

const TIPO_LABELS: Record<string, { label: string; color: string }> = {
  pre_carregamento: { label: 'Pré-Carregamento', color: 'text-amber-600' },
  carregamento: { label: 'Carregamento', color: 'text-blue-600' },
  descarregamento: { label: 'Descarregamento', color: 'text-green-600' }
};

const ConferenteScanner: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const isOnline = useOnlineStatus();

  const [conferencia, setConferencia] = useState<Conferencia | null>(null);
  const [resumoLeituras, setResumoLeituras] = useState<ResumoLeituras | null>(null);
  const [leituras, setLeituras] = useState<ConferenciaLeitura[]>([]);
  const [codigo, setCodigo] = useState('');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [tipoLeitura, setTipoLeitura] = useState('codigo_barras');
  const [lastResult, setLastResult] = useState<{
    tipo: 'ok' | 'duplicado' | 'nao_encontrado' | 'erro' | 'offline' | 'area_divergente';
    mensagem: string;
  } | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [erroCarregamento, setErroCarregamento] = useState('');
  const [showConfirmFinalizar, setShowConfirmFinalizar] = useState(false);
  const [erroFinalizar, setErroFinalizar] = useState('');
  const [pendentesCount, setPendentesCount] = useState(0);
  const [offlineLeituras, setOfflineLeituras] = useState<LeituraPendente[]>([]);
  const [syncing, setSyncing] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scanningCameraRef = useRef<boolean>(false);
  const animationFrameRef = useRef<number | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const carregarDados = useCallback(async () => {
    if (!id) return;
    setErroCarregamento('');

    const idNum = parseInt(id);

    // Conferencias offline usam IDs negativos temporarios
    if (idNum < 0) {
      try {
        const pendentes = await listarConferenciasPendentes();
        const confOffline = pendentes.find(c => c.temp_id === idNum);
        if (confOffline) {
          setConferencia({
            id: confOffline.temp_id,
            tipo: confOffline.tipo,
            status: 'em_andamento',
            placa_veiculo: confOffline.placa_veiculo || '',
            manifesto: confOffline.manifesto || '',
            transportadora: confOffline.transportadora || '',
            rota: confOffline.rota || '',
            observacoes: confOffline.observacoes || '',
            total_itens_esperado: 0,
            total_itens_conferido: 0,
            criado_em: confOffline.criado_em,
            atualizado_em: confOffline.criado_em,
            empresa_id: 0,
            total_divergencias: 0,
            responsavel_id: 0,
          } as any);
          // Contar leituras offline existentes para esta conferencia
          let offlineCount = 0;
          try {
            const leitOffline = await listarLeiturasPendentes(confOffline.temp_id);
            offlineCount = leitOffline.length;
          } catch { /* ignorar */ }
          setResumoLeituras({
            total_leituras: offlineCount,
            leituras_ok: 0,
            duplicados: 0,
            nao_encontrados: 0,
            avarias: 0,
            erros: 0,
            itens_distintos: 0,
          } as any);
        } else {
          setErroCarregamento('Conferência offline não encontrada');
        }
      } catch {
        setErroCarregamento('Erro ao carregar conferência offline');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const result = await buscarConferenciaConferente(idNum);
      if (result.success) {
        setConferencia(result.conferencia);
        setResumoLeituras(result.resumo_leituras);
        salvarConferenciaCache(idNum, result).catch(() => {});
      }

      try {
        const leitData = await listarLeiturasConferente(idNum);
        if (leitData.success) setLeituras(leitData.leituras);
      } catch {
        // Leituras são opcionais
      }
    } catch (error: any) {
      try {
        const cached = await buscarConferenciaCache(idNum);
        if (cached) {
          setConferencia(cached.conferencia);
          setResumoLeituras(cached.resumo_leituras);
        } else {
          setErroCarregamento(error.message || 'Erro ao carregar dados da conferência. Verifique sua conexão.');
        }
      } catch {
        setErroCarregamento(error.message || 'Erro ao carregar dados da conferência. Verifique sua conexão.');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Load offline pending readings
  const carregarPendentes = useCallback(async () => {
    if (!id) return;
    try {
      const pendentes = await listarLeiturasPendentes(parseInt(id));
      setOfflineLeituras(pendentes);
      const total = await contarLeiturasPendentes();
      setPendentesCount(total);
    } catch {
      // Silenciar
    }
  }, [id]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  useEffect(() => {
    carregarPendentes();
  }, [carregarPendentes]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && pendentesCount > 0) {
      handleSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await sincronizarPendentes();
      await carregarDados();
      await carregarPendentes();
    } catch (err: any) {
      console.error('Erro ao sincronizar:', err);
      setErroCarregamento('Erro ao sincronizar dados. Verifique sua conexão.');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (!loading) inputRef.current?.focus();
  }, [loading]);

  // Gerenciamento da câmera para leitura em tempo real
  useEffect(() => {
    if (!showCamera) return;

    let mounted = true;
    let activeStream: MediaStream | null = null;
    let barcodeInterval: ReturnType<typeof setInterval> | null = null;

    const onDetected = (data: string) => {
      if (!mounted) return;
      scanningCameraRef.current = false;
      setCodigo(data);
      setShowCamera(false);
      vibrar([200]);
      // Auto-submit após leitura
      setTimeout(() => formRef.current?.requestSubmit(), 100);
    };

    const scanFrame = () => {
      if (!scanningCameraRef.current || !mounted || !videoRef.current || !canvasRef.current) return;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx || !video.videoWidth || !video.videoHeight) {
        animationFrameRef.current = requestAnimationFrame(scanFrame);
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });

      if (code?.data) {
        onDetected(code.data);
        return;
      }

      if (scanningCameraRef.current && mounted) {
        animationFrameRef.current = requestAnimationFrame(scanFrame);
      }
    };

    const initCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });

        activeStream = stream;

        if (!mounted || !videoRef.current) {
          stream.getTracks().forEach(t => t.stop());
          activeStream = null;
          return;
        }

        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        scanningCameraRef.current = true;
        scanFrame();

        if ('BarcodeDetector' in window) {
          barcodeInterval = setInterval(async () => {
            if (!scanningCameraRef.current || !videoRef.current || !mounted) return;
            try {
              const detector = new (window as any).BarcodeDetector();
              const barcodes = await detector.detect(videoRef.current);
              if (barcodes.length > 0 && scanningCameraRef.current && mounted) {
                onDetected(barcodes[0].rawValue);
              }
            } catch { /* silenciar erros */ }
          }, 500);
        }
      } catch {
        if (mounted) {
          setCameraError('Não foi possível acessar a câmera. Verifique as permissões.');
        }
      }
    };

    initCamera();

    return () => {
      mounted = false;
      scanningCameraRef.current = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (barcodeInterval) clearInterval(barcodeInterval);
      if (activeStream) {
        activeStream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCamera]);

  const conferenciaFinalizada = conferencia?.status === 'concluida' || conferencia?.status === 'cancelada';

  const vibrar = (pattern: number[]) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  };

  const handleScan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !codigo.trim() || scanning || conferenciaFinalizada) return;

    setScanning(true);
    setLastResult(null);

    const idNum = parseInt(id);

    // If offline OR conference is offline-created (negative ID), save to local queue
    if (!isOnline || idNum < 0) {
      try {
        await salvarLeituraPendente({
          conferencia_id: idNum,
          tipo_leitura: tipoLeitura,
          codigo_lido: codigo.trim(),
          quantidade: 1,
          lido_em: new Date().toISOString(),
        });
        setLastResult({ tipo: 'offline', mensagem: idNum < 0 ? 'Conferencia offline. Leitura salva localmente.' : 'Leitura salva localmente. Sera enviada ao reconectar.' });
        vibrar([200]);
        playSuccessSound();
        // Atualizar contadores locais
        setResumoLeituras(prev => prev ? {
          ...prev,
          total_leituras: prev.total_leituras + 1,
        } : prev);
        await carregarPendentes();
      } catch {
        setLastResult({ tipo: 'erro', mensagem: 'Erro ao salvar leitura offline' });
        vibrar([300, 100, 300]);
        playErrorSound();
      } finally {
        setCodigo('');
        setScanning(false);
        inputRef.current?.focus();
      }
      return;
    }

    try {
      const result = await registrarLeituraConferente(parseInt(id), {
        tipo_leitura: tipoLeitura,
        codigo_lido: codigo.trim(),
        quantidade: 1
      });

      if (result.success) {
        if (result.duplicado) {
          setLastResult({ tipo: 'duplicado', mensagem: 'Código já lido anteriormente' });
          vibrar([100, 50, 100]);
          playDuplicateSound();
        } else if (result.encontrado) {
          setLastResult({ tipo: 'ok', mensagem: 'Item conferido com sucesso!' });
          vibrar([200]);
          playSuccessSound();
        } else {
          setLastResult({ tipo: 'nao_encontrado', mensagem: 'Código não encontrado na lista' });
          vibrar([100, 50, 100, 50, 100]);
          playDivergenceSound();
        }

        await carregarDados();
      }
    } catch (error: any) {
      // Network error - save offline
      if (!isOnline || error.message?.includes('fetch') || error.message?.includes('network') || error.message?.includes('Failed')) {
        try {
          await salvarLeituraPendente({
            conferencia_id: parseInt(id),
            tipo_leitura: tipoLeitura,
            codigo_lido: codigo.trim(),
            quantidade: 1,
            lido_em: new Date().toISOString(),
          });
          setLastResult({ tipo: 'offline', mensagem: 'Sem conexão. Leitura salva localmente.' });
          vibrar([200]);
          playSuccessSound();
          setResumoLeituras(prev => prev ? {
            ...prev,
            total_leituras: prev.total_leituras + 1,
          } : prev);
          await carregarPendentes();
        } catch {
          setLastResult({ tipo: 'erro', mensagem: 'Erro ao salvar leitura' });
          vibrar([300, 100, 300]);
          playErrorSound();
        }
      } else {
        // Verificar se é erro de área de entrega divergente
        const isAreaDivergente = error.message && (
          error.message.includes('Área de entrega divergente') ||
          error.message.includes('não corresponde a nenhuma área de entrega')
        );
        if (isAreaDivergente) {
          setLastResult({ tipo: 'area_divergente', mensagem: error.message });
          vibrar([300, 100, 300, 100, 300]);
          playDivergenceSound();
        } else {
          setLastResult({ tipo: 'erro', mensagem: error.message || 'Erro ao processar leitura' });
          vibrar([300, 100, 300]);
          playErrorSound();
        }
      }
    } finally {
      setCodigo('');
      setScanning(false);
      inputRef.current?.focus();
    }
  };

  const handleFinalizar = async () => {
    if (!id || !conferencia) return;
    setErroFinalizar('');

    const idNum = parseInt(id);

    if (idNum < 0) {
      setErroFinalizar('Esta conferência foi criada offline. Sincronize os dados primeiro para poder finalizá-la.');
      return;
    }

    if (!isOnline) {
      setErroFinalizar('Sem conexão. Conecte a internet para finalizar a conferência.');
      return;
    }

    try {
      await finalizarConferenciaConferente(idNum);
      setShowConfirmFinalizar(false);
      await carregarDados();
    } catch (error: any) {
      setErroFinalizar(error.message || 'Erro ao finalizar conferência');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-green-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!conferencia) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        {erroCarregamento ? (
          <>
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-4 text-center w-full max-w-sm">
              <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-3" />
              <p className="text-base text-red-700">{erroCarregamento}</p>
            </div>
            <button
              onClick={() => { setLoading(true); carregarDados(); }}
              className="px-6 py-3 bg-green-600 text-white rounded-xl text-base font-medium hover:bg-green-700 transition mb-3 active:bg-green-800"
            >
              Tentar novamente
            </button>
          </>
        ) : (
          <p className="text-gray-500 mb-4 text-base">Conferência não encontrada</p>
        )}
        <button onClick={() => navigate('/conferente')} className="text-green-600 font-medium text-base py-2">
          Voltar
        </button>
      </div>
    );
  }

  const progresso = conferencia.total_itens_esperado > 0
    ? Math.round((conferencia.total_itens_conferido / conferencia.total_itens_esperado) * 100)
    : 0;
  const tipoInfo = TIPO_LABELS[conferencia.tipo] || TIPO_LABELS.carregamento;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Offline Banner */}
      {!isOnline && (
        <div className="bg-yellow-500 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium safe-top">
          <WifiOff className="w-4 h-4" />
          Modo offline - leituras serao salvas localmente
        </div>
      )}

      {/* Pending Sync Banner */}
      {pendentesCount > 0 && isOnline && (
        <div className="bg-blue-500 text-white px-4 py-2.5 flex items-center justify-between text-sm">
          <span>{offlineLeituras.length > 0 ? `${offlineLeituras.length} desta | ` : ''}{pendentesCount} total pendente{pendentesCount > 1 ? 's' : ''}</span>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-xl hover:bg-white/30 transition text-sm active:bg-white/40"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar'}
          </button>
        </div>
      )}

      {/* Header */}
      <div className={`bg-green-600 text-white px-4 py-4 ${isOnline ? 'safe-top' : ''}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/conferente')} className="p-2 hover:bg-white/10 rounded-lg transition" aria-label="Voltar ao dashboard">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <p className="text-base font-bold">
                {conferencia.id < 0 ? 'Offline' : `#${conferencia.id}`} - {tipoInfo.label}
              </p>
              <p className="text-sm text-green-200">
                {conferencia.placa_veiculo && `${conferencia.placa_veiculo} | `}
                {conferencia.manifesto || 'Sem manifesto'}
              </p>
            </div>
          </div>

          {!conferenciaFinalizada && (
            <button
              onClick={() => setShowConfirmFinalizar(true)}
              className="px-4 py-2 bg-white text-green-700 rounded-xl text-sm font-semibold hover:bg-green-50 transition active:scale-95"
            >
              Finalizar
            </button>
          )}
        </div>

        {/* Barra de progresso */}
        <div className="mt-3">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-green-100">{conferencia.total_itens_conferido}/{conferencia.total_itens_esperado} itens</span>
            <span className="font-semibold">{progresso}%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${progresso >= 100 ? 'bg-white' : 'bg-green-200'}`}
              style={{ width: `${Math.min(progresso, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Aviso finalizada */}
      {conferenciaFinalizada && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3 flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
          <p className="text-base text-yellow-800 font-medium">
            Conferência {conferencia.status === 'concluida' ? 'concluída' : 'cancelada'}
          </p>
        </div>
      )}

      {/* Area de leitura */}
      {!conferenciaFinalizada && (
        <div className="bg-white border-b border-gray-200 p-4">
          <form ref={formRef} onSubmit={handleScan} className="space-y-3">
            <div className="flex gap-2">
              <select
                value={tipoLeitura}
                onChange={(e) => setTipoLeitura(e.target.value)}
                className="border border-gray-300 rounded-xl px-3 py-3 text-base focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none bg-white"
                aria-label="Tipo de leitura"
              >
                <option value="codigo_barras">Cod. Barras</option>
                <option value="qrcode">QR Code</option>
                <option value="manual">Manual</option>
              </select>
              <div className="flex-1 relative">
                <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  placeholder="Leia ou digite o código..."
                  aria-label="Código de barras ou QR Code"
                  className="w-full pl-10 pr-14 py-3 border-2 border-gray-300 rounded-xl text-base font-mono focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                  autoFocus
                  autoComplete="off"
                  inputMode="text"
                />
                <button
                  type="button"
                  onClick={() => { setCameraError(null); setShowCamera(true); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 rounded-xl text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors active:bg-green-100"
                  title="Abrir câmera para escanear"
                  aria-label="Abrir câmera para escanear"
                >
                  <Camera className="w-6 h-6" />
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={!codigo.trim() || scanning}
              className="w-full bg-green-600 text-white py-3.5 rounded-xl font-semibold text-base hover:bg-green-700 transition disabled:opacity-50 flex items-center justify-center gap-2 active:bg-green-800"
            >
              <PackageCheck className="w-6 h-6" />
              {scanning ? 'Lendo...' : 'Conferir'}
            </button>
          </form>

          {/* Resultado */}
          {lastResult && (
            <div className={`mt-3 p-4 rounded-xl flex items-start gap-3 ${
              lastResult.tipo === 'ok' ? 'bg-green-50 border border-green-200' :
              lastResult.tipo === 'duplicado' ? 'bg-yellow-50 border border-yellow-200' :
              lastResult.tipo === 'offline' ? 'bg-blue-50 border border-blue-200' :
              lastResult.tipo === 'area_divergente' ? 'bg-orange-50 border-2 border-orange-400' :
              'bg-red-50 border border-red-200'
            }`}>
              {lastResult.tipo === 'ok' && <CheckCircle className="w-7 h-7 text-green-600 flex-shrink-0 mt-0.5" />}
              {lastResult.tipo === 'duplicado' && <Copy className="w-7 h-7 text-yellow-600 flex-shrink-0 mt-0.5" />}
              {lastResult.tipo === 'nao_encontrado' && <XCircle className="w-7 h-7 text-red-600 flex-shrink-0 mt-0.5" />}
              {lastResult.tipo === 'erro' && <AlertTriangle className="w-7 h-7 text-red-600 flex-shrink-0 mt-0.5" />}
              {lastResult.tipo === 'offline' && <CloudOff className="w-7 h-7 text-blue-600 flex-shrink-0 mt-0.5" />}
              {lastResult.tipo === 'area_divergente' && <MapPin className="w-7 h-7 text-orange-600 flex-shrink-0 mt-0.5" />}
              <div className="flex-1">
                <p className={`font-semibold text-base ${
                  lastResult.tipo === 'ok' ? 'text-green-800' :
                  lastResult.tipo === 'duplicado' ? 'text-yellow-800' :
                  lastResult.tipo === 'offline' ? 'text-blue-800' :
                  lastResult.tipo === 'area_divergente' ? 'text-orange-800' : 'text-red-800'
                }`}>
                  {lastResult.tipo === 'ok' ? 'Conferido!' :
                   lastResult.tipo === 'duplicado' ? 'Duplicado' :
                   lastResult.tipo === 'nao_encontrado' ? 'Não Encontrado' :
                   lastResult.tipo === 'offline' ? 'Salvo Offline' :
                   lastResult.tipo === 'area_divergente' ? 'Área Divergente!' : 'Erro'}
                </p>
                <p className="text-sm text-gray-600">{lastResult.mensagem}</p>
                {lastResult.tipo === 'area_divergente' && (
                  <p className="text-xs text-orange-700 font-medium mt-1">
                    A etiqueta foi rejeitada. Verifique a área de entrega correta.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Resumo */}
      {resumoLeituras && (
        <div className="grid grid-cols-4 gap-2 p-4">
          <div className="bg-white rounded-xl p-3 text-center border border-gray-100">
            <p className="text-xl font-bold text-gray-900">{resumoLeituras.total_leituras}</p>
            <p className="text-xs text-gray-500">Total</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center border border-gray-100">
            <p className="text-xl font-bold text-green-600">{resumoLeituras.leituras_ok}</p>
            <p className="text-xs text-gray-500">OK</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center border border-gray-100">
            <p className="text-xl font-bold text-yellow-600">{resumoLeituras.duplicados}</p>
            <p className="text-xs text-gray-500">Duplic.</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center border border-gray-100">
            <p className="text-xl font-bold text-red-600">{resumoLeituras.nao_encontrados}</p>
            <p className="text-xs text-gray-500">N/ Enc.</p>
          </div>
        </div>
      )}

      {/* Leituras pendentes offline - só mostra separado quando há leituras do servidor */}
      {offlineLeituras.length > 0 && leituras.length > 0 && (
        <div className="px-4 pt-3">
          <h3 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-2">
            <CloudOff className="w-4 h-4" />
            Leituras Pendentes ({offlineLeituras.length})
          </h3>
          <div className="divide-y divide-blue-100 bg-blue-50 rounded-xl overflow-hidden border border-blue-200 mb-3">
            {offlineLeituras.map((l, idx) => (
              <div key={l.id || idx} className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CloudOff className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="text-sm font-mono font-medium text-gray-800">{l.codigo_lido}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(l.lido_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
                <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                  {l.status === 'enviando' ? 'ENV.' : l.status === 'erro' ? 'ERRO' : 'PEND.'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Histórico de leituras */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 pb-2">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Últimas Leituras</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {leituras.length === 0 && offlineLeituras.length === 0 ? (
            <p className="text-center text-gray-400 text-base py-8">Nenhuma leitura realizada</p>
          ) : (
            <>
              {leituras.map(leitura => (
                <div key={leitura.id} className="px-4 py-3 flex items-center justify-between bg-white">
                  <div className="flex items-center gap-3">
                    {leitura.resultado === 'ok' && <CheckCircle className="w-5 h-5 text-green-500" />}
                    {leitura.resultado === 'duplicado' && <Copy className="w-5 h-5 text-yellow-500" />}
                    {leitura.resultado === 'nao_encontrado' && <XCircle className="w-5 h-5 text-red-500" />}
                    {(leitura.resultado === 'avaria' || leitura.resultado === 'erro') && <AlertTriangle className="w-5 h-5 text-orange-500" />}
                    <div>
                      <p className="text-sm font-mono font-medium text-gray-800">{leitura.codigo_lido}</p>
                      <p className="text-xs text-gray-400">
                        {new Date(leitura.lido_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    leitura.resultado === 'ok' ? 'bg-green-100 text-green-700' :
                    leitura.resultado === 'duplicado' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {leitura.resultado === 'ok' ? 'OK' :
                     leitura.resultado === 'duplicado' ? 'DUP' :
                     leitura.resultado === 'nao_encontrado' ? 'N/E' : 'ERR'}
                  </span>
                </div>
              ))}
              {/* Mostrar leituras offline na secao principal quando nao ha leituras do servidor */}
              {leituras.length === 0 && offlineLeituras.map((l, idx) => (
                <div key={l.id || idx} className="px-4 py-3 flex items-center justify-between bg-white">
                  <div className="flex items-center gap-3">
                    <CloudOff className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-sm font-mono font-medium text-gray-800">{l.codigo_lido}</p>
                      <p className="text-xs text-gray-400">
                        {new Date(l.lido_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                    {l.status === 'enviando' ? 'ENV.' : l.status === 'erro' ? 'ERRO' : 'PEND.'}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Modal de confirmação de finalização */}
      {showConfirmFinalizar && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center p-0">
          <div className="bg-white rounded-t-2xl w-full max-w-lg p-6 pb-8 safe-bottom">
            <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
            <h4 className="font-semibold text-gray-800 text-lg mb-2">Finalizar Conferência</h4>
            <p className="text-base text-gray-600 mb-5">
              Tem certeza que deseja finalizar esta conferência? Esta ação não pode ser desfeita.
            </p>
            {erroFinalizar && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                <p className="text-sm text-red-700">{erroFinalizar}</p>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowConfirmFinalizar(false); setErroFinalizar(''); }}
                className="flex-1 px-4 py-3.5 border border-gray-300 text-gray-700 rounded-xl font-medium text-base hover:bg-gray-50 transition active:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleFinalizar}
                className="flex-1 px-4 py-3.5 bg-green-600 text-white rounded-xl font-medium text-base hover:bg-green-700 transition active:bg-green-800"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal da câmera */}
      {showCamera && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                <Camera className="w-5 h-5 text-green-600" />
                Escanear Código
              </h4>
              <button
                onClick={() => setShowCamera(false)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="relative w-full aspect-[4/3] bg-black overflow-hidden">
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                playsInline
                muted
              />
              <canvas ref={canvasRef} className="hidden" />

              {!cameraError && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-52 h-52 border-3 border-green-500 rounded-xl"
                  style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)' }}
                />
              )}
            </div>

            <div className="p-4">
              {cameraError ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                  <AlertTriangle className="w-5 h-5 text-red-500 mx-auto mb-1" />
                  <p className="text-sm text-red-700">{cameraError}</p>
                </div>
              ) : (
                <p className="text-center text-gray-500 text-sm flex items-center justify-center gap-2">
                  <Crosshair className="w-4 h-4" />
                  Aponte a câmera para o QR Code ou código de barras
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConferenteScanner;
